import React, {useCallback, useEffect, useMemo, useRef} from 'react';

import {EEventSource} from 'coreClasses/events/IEvents';
import {useProducts, useSearchMgrInstance} from 'coreHooks';

import {NEXT_DIRECTION, PREV_DIRECTION} from 'brand/app/constants';
import {VisibilityContextProvider} from 'brand/app/contexts/viewportProductContext';
import {useCategoryContext} from 'brand/app/pages/category/context/CategoryContext';
import Pagination from 'brandComponents/Pagination';
import {ProductsGrid} from 'brandComponents/ProductsGrid';
import useViewItemListEvent from 'brandHooks/useViewItemListEvent';

const ProductListGridContainer = () => {
	const {searchType, searchInstanceKey, isPrevPageLoaded} = useCategoryContext(true) || {};
	const searchMgr = useSearchMgrInstance({
		searchType,
		key: searchInstanceKey,
		compare: ['pages'],
	});
	const searchModel = searchMgr.getStoredSearchModel();
	const source = searchModel?.getSearchQuery() ? EEventSource.SEARCH_RESULTS : EEventSource.PLP;
	const cgid = searchModel?.getSelectedRefinementById('cgid');
	const productHitIds = searchModel?.getAllHits().map((productHit) => productHit.getProductId()) || [];
	const products = useProducts(productHitIds, true);
	const {handleProductVisibility} = useViewItemListEvent(products, source, cgid);
	// we need ref because using isPrevPageLoaded directly, the observer is registered with the initial value
	const isPrevPageLoadedRef = useRef(isPrevPageLoaded);

	const visibilityContextValue = useMemo(() => ({
		handleVisibilityChange: handleProductVisibility
	}), [handleProductVisibility]);

	useEffect(() => {
		isPrevPageLoadedRef.current = isPrevPageLoaded;
	}, [isPrevPageLoaded]);

	const handleIntersect = useCallback((pageNumber: number) => (entries: IntersectionObserverEntry[]) => {
		entries.forEach((entry) => {
			if (isPrevPageLoadedRef.current && entry.isIntersecting) {
				searchMgr.setPageNumber(pageNumber);
			}
		});
	}, [searchMgr]);

	return (
		<>
			{searchModel?.getPrevPageNumberToLoad() && (
				<Pagination
					totalItems={searchModel?.getTotalProductsNumber()}
					totalItemsLoaded={searchModel?.getTotalLoadedProductsNumber()}
					pageToLoad={searchModel?.getPrevPageNumberToLoad()}
					direction={PREV_DIRECTION}
					searchType={searchType}
					searchInstanceKey={searchInstanceKey}
				/>
			)}

			<VisibilityContextProvider value={visibilityContextValue}>
				<ProductsGrid>
					{searchModel?.getPages().map((pageModel) => (
						<ProductsGrid.SearchHits
							key={`search-hits--${cgid}-${pageModel.getPageNumber()}`}
							page={pageModel.getPageNumber()}
							pageSize={searchModel.getPageSize()}
							sourceOverwritten={source}
							onIntersect={handleIntersect(pageModel.getPageNumber())}
							searchHits={pageModel.getHits()}
							lowestPage={searchModel.getLowestLoadedPageNumber()}
						/>
					))}
				</ProductsGrid>
			</VisibilityContextProvider>

			{searchModel?.getNextPageNumberToLoad() && (
				<Pagination
					totalItems={searchModel?.getTotalProductsNumber()}
					totalItemsLoaded={searchModel?.getTotalLoadedProductsNumber()}
					pageToLoad={searchModel?.getNextPageNumberToLoad()}
					direction={NEXT_DIRECTION}
					searchType={searchType}
					searchInstanceKey={searchInstanceKey}
				/>
			)}
		</>
	);
};

export default ProductListGridContainer;
